<template>
  <div class="time-picker-wrapper">
    <el-time-picker 
      ref="timePicker"
      v-model="selectedTime" 
      value-format="HH:mm" 
      format="HH:mm"
      @change="handleTimeChange"
      :picker-options="{
        selectableRange: '00:00:00 - 23:59:59'
      }" 
      :style="{ width: '100%' }"
      :placeholder="displayPlaceholder"
      :disabled="!disabled"
    >
    </el-time-picker>
  </div>
</template>

<script>
export default {
  props: {
    // 接收外部传入的值，格式如 "08:00-08:00" 或 "08:00 - 08:00"
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedTime: null,      // 实际选中的时间，如 "08:00"
      displayPlaceholder: '选择日报推送开始时间',  // 占位符
      observer: null           // MutationObserver 实例
    }
  },
  computed: {
    // 计算显示的文本
    displayText() {
      if (this.selectedTime) {
        return `${this.selectedTime} - ${this.selectedTime}`;
      }
      return '';
    }
  },
  watch: {
    // 监听外部传入的 value 变化
    value: {
      handler(newVal) {
        if (newVal) {
          let timeValue = newVal;
          if (newVal.includes('-')) {
            timeValue = newVal.split('-')[0].trim();
          }
          // 验证是否为有效时间格式
          if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeValue)) {
            this.selectedTime = timeValue;
            // 延迟更新，确保 Element UI 内部渲染完成
            this.$nextTick(() => {
              this.updateInputDisplay();
              // 强制再更新一次，防止被覆盖
              setTimeout(() => {
                this.updateInputDisplay();
              }, 50);
            });
          }
        } else {
          this.selectedTime = null;
          this.$nextTick(() => {
            this.updateInputDisplay();
          });
        }
      },
      immediate: true
    },
    // 监听 selectedTime 变化，更新显示
    selectedTime() {
      this.$nextTick(() => {
        this.updateInputDisplay();
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateInputDisplay();
      this.bindInputEvents();
      // 添加 MutationObserver 监听 DOM 变化，防止被覆盖
      this.observeInputChanges();
    });
  },
  beforeDestroy() {
    this.unbindInputEvents();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    // 更新输入框的显示值（增强版）
    updateInputDisplay() {
      if (!this.$refs.timePicker) return;
      
      const input = this.$refs.timePicker.$el.querySelector('input');
      if (input) {
        // 直接设置 value 属性
        if (this.displayText) {
          input.value = this.displayText;
        } else {
          input.value = '';
        }
        // 触发 input 事件，让 Element UI 感知到变化
        const event = new Event('input', { bubbles: true });
        input.dispatchEvent(event);
      }
    },
    
    // 绑定输入框事件，防止用户编辑时被覆盖
    bindInputEvents() {
      if (!this.$refs.timePicker) return;
      
      const input = this.$refs.timePicker.$el.querySelector('input');
      if (input) {
        // 用户聚焦时，暂时移除显示覆盖
        input.addEventListener('focus', this.handleInputFocus);
        // 用户失焦时，重新设置显示
        input.addEventListener('blur', this.handleInputBlur);
        // 防止用户手动输入
        input.addEventListener('input', this.preventManualInput);
      }
    },
    
    // 解绑事件
    unbindInputEvents() {
      if (!this.$refs.timePicker) return;
      
      const input = this.$refs.timePicker.$el.querySelector('input');
      if (input) {
        input.removeEventListener('focus', this.handleInputFocus);
        input.removeEventListener('blur', this.handleInputBlur);
        input.removeEventListener('input', this.preventManualInput);
      }
    },
    
    // 监听输入框的变化，防止被 Element UI 覆盖
    observeInputChanges() {
      if (!this.$refs.timePicker) return;
      
      const input = this.$refs.timePicker.$el.querySelector('input');
      if (!input) return;
      
      // 使用 MutationObserver 监听属性变化
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            // 如果当前有 displayText 且输入框的值不是 displayText，则重新设置
            if (this.displayText && input.value !== this.displayText && !input.matches(':focus')) {
              input.value = this.displayText;
            }
          }
        });
      });
      
      this.observer.observe(input, { attributes: true });
    },
    
    // 聚焦时，临时显示原始时间值，方便用户编辑
    handleInputFocus() {
      if (!this.$refs.timePicker) return;
      const input = this.$refs.timePicker.$el.querySelector('input');
      if (input && this.selectedTime) {
        // 暂时断开 observer，避免循环触发
        if (this.observer) {
          this.observer.disconnect();
        }
        input.value = this.selectedTime;
        // 重新连接 observer
        setTimeout(() => {
          if (this.observer && input) {
            this.observer.observe(input, { attributes: true });
          }
        }, 0);
      }
    },
    
    // 失焦时，重新显示带横杠的格式
    handleInputBlur() {
      this.$nextTick(() => {
        this.updateInputDisplay();
      });
    },
    
    // 阻止用户手动输入（保持只读效果）
    preventManualInput(e) {
      // 如果用户输入的不是时间选择器内部触发的变化，则阻止
      if (!e.isTrusted) return;
      // 可选：显示提示或不做处理，让组件自己处理
    },
    
    // 时间变化时的处理
    handleTimeChange(time) {
      if (!time) {
        this.$emit('input', '');
        return;
      }
      // 保存为 "08:00-08:00" 格式（无空格）
      const formattedValue = `${time}-${time}`;
      this.$emit('input', formattedValue);
      // 更新显示
      this.$nextTick(() => {
        this.updateInputDisplay();
      });
    }
  }
}
</script>

<style scoped>
.time-picker-wrapper {
  width: 100%;
}

/* 可选：添加一些样式优化显示 */
/deep/ .el-time-picker .el-input__inner {
  text-align: center;
}
</style>